/* global atlas */
import bootstrap from "bootstrap/dist/js/bootstrap.min.js";
import {
  MAP_BASE_STYLE,
  MAP_BASE_STYLE_OVERRIDES,
  MAP_MAX_ZOOM,
  MAP_MIN_ZOOM,
  clampZoom,
} from "./map-controller-utils";
import {
  buildArrowDataUri,
  getStoredReturnGeometry,
  getStoredRouteGeometry,
  lightenHexColor,
  sanitizeHexColor,
} from "./route-geometry";
import { escapeHtml } from "../../utils/formatters";

export function createLiveMapController(options) {
  const runtimeOptions = options && typeof options === "object" ? options : {};
  const mapElement =
    runtimeOptions.mapElement ||
    (runtimeOptions.rootElement?.querySelector
      ? runtimeOptions.rootElement.querySelector("#live-map")
      : document.getElementById("live-map"));
  const subscriptionKey = String(runtimeOptions.initialSubscriptionKey || "").trim();
  const initialStyle = String(runtimeOptions.initialStyle || MAP_BASE_STYLE).trim() || MAP_BASE_STYLE;

  const DEFAULT_CENTER = [-3.7038, 40.4168];
  const DEFAULT_ZOOM = 11.5;
  const DEFAULT_PADDING = 72;
  const DELIVERY_LINE_FILTER = ["all", ["==", ["geometry-type"], "LineString"], ["!=", ["get", "isReturn"], true]];
  const RETURN_LINE_FILTER = ["==", ["get", "isReturn"], true];

  const state = {
    arrowIconColors: new Set(),
    comparisonRoutePositions: [],
    comparisonRouteSource: null,
    currentMarkers: [],
    currentPositions: new Map(),
    destroyed: false,
    historySource: null,
    lastBounds: null,
    map: null,
    mapReady: null,
    resizeObserver: null,
    resizeTimer: null,
    traceSource: null,
  };

  function ensureMap() {
    if (!window.atlas) {
      throw new Error("Azure Maps SDK no esta cargado.");
    }

    if (!mapElement) {
      throw new Error("No se encontro el contenedor del mapa live.");
    }

    if (!subscriptionKey) {
      throw new Error("Falta la Azure Maps key para la vista live.");
    }

    if (state.map) {
      return state.mapReady;
    }

    state.destroyed = false;
    state.map = new atlas.Map(mapElement, {
      authOptions: {
        authType: "subscriptionKey",
        subscriptionKey,
      },
      center: DEFAULT_CENTER,
      style: initialStyle,
      styleOverrides: MAP_BASE_STYLE_OVERRIDES,
      view: "Auto",
      zoom: DEFAULT_ZOOM,
      minZoom: MAP_MIN_ZOOM,
      maxZoom: MAP_MAX_ZOOM,
    });
    const mapInstance = state.map;

    state.mapReady = new Promise((resolve) => {
      mapInstance.events.add("ready", function () {
        if (state.destroyed || state.map !== mapInstance) {
          resolve();
          return;
        }

        state.traceSource = new atlas.source.DataSource();
        state.historySource = new atlas.source.DataSource();
        state.comparisonRouteSource = new atlas.source.DataSource();

        mapInstance.sources.add(state.traceSource);
        mapInstance.sources.add(state.historySource);
        mapInstance.sources.add(state.comparisonRouteSource);

        mapInstance.layers.add(
          new atlas.layer.LineLayer(state.traceSource, "live-traces-glow", {
            blur: 1.1,
            strokeColor: ["get", "glowColor"],
            strokeOpacity: 0.24,
            strokeWidth: 9,
          })
        );

        mapInstance.layers.add(
          new atlas.layer.LineLayer(state.traceSource, "live-traces", {
            lineCap: "round",
            lineJoin: "round",
            strokeColor: ["get", "strokeColor"],
            strokeOpacity: 0.96,
            strokeWidth: 4.5,
          })
        );

        mapInstance.layers.add(
          new atlas.layer.LineLayer(state.comparisonRouteSource, "live-comparison-route-glow", {
            blur: 1,
            lineCap: "round",
            lineJoin: "round",
            strokeColor: ["get", "glowColor"],
            strokeOpacity: 0.16,
            strokeWidth: 8,
            filter: DELIVERY_LINE_FILTER,
          })
        );

        mapInstance.layers.add(
          new atlas.layer.LineLayer(state.comparisonRouteSource, "live-comparison-route", {
            lineCap: "round",
            lineJoin: "round",
            strokeDashArray: [1.5, 1.2],
            strokeColor: ["get", "strokeColor"],
            strokeOpacity: 0.72,
            strokeWidth: 3.5,
            filter: DELIVERY_LINE_FILTER,
          })
        );

        mapInstance.layers.add(
          new atlas.layer.LineLayer(state.comparisonRouteSource, "live-comparison-return", {
            lineCap: "round",
            lineJoin: "round",
            strokeDashArray: [0.1, 2.4],
            strokeColor: ["get", "strokeColor"],
            strokeOpacity: 0.65,
            strokeWidth: 2.5,
            filter: RETURN_LINE_FILTER,
          })
        );

        mapInstance.layers.add(
          new atlas.layer.BubbleLayer(state.historySource, "live-history-points", {
            color: ["get", "pointColor"],
            opacity: 0.34,
            radius: 4,
            strokeColor: "#ffffff",
            strokeWidth: 1.4,
          })
        );

        mapInstance.layers.add(
          new atlas.layer.SymbolLayer(state.traceSource, "live-trace-arrows", {
            placement: "line",
            lineSpacing: 120,
            iconOptions: {
              image: ["concat", "route-arrow-", ["get", "routeColorHex"]],
              allowOverlap: true,
              ignorePlacement: true,
              rotationAlignment: "map",
              anchor: "center",
              size: 1.0,
              opacity: 0.9,
            },
            filter: ["==", ["geometry-type"], "LineString"],
          })
        );

        mapInstance.layers.add(
          new atlas.layer.SymbolLayer(state.comparisonRouteSource, "live-comparison-route-arrows", {
            placement: "line",
            lineSpacing: 120,
            iconOptions: {
              image: ["concat", "route-arrow-", ["get", "routeColorHex"]],
              allowOverlap: true,
              ignorePlacement: true,
              rotationAlignment: "map",
              anchor: "center",
              size: 1.0,
              opacity: 0.9,
            },
            filter: DELIVERY_LINE_FILTER,
          })
        );

        observeResize();
        resolve();
      });
    });

    return state.mapReady;
  }

  function observeResize() {
    if (!window.ResizeObserver || !mapElement) return;

    state.resizeObserver = new ResizeObserver(() => {
      clearTimeout(state.resizeTimer);
      state.resizeTimer = setTimeout(() => {
        if (state.destroyed || !state.map) return;
        state.map.resize();
      }, 160);
    });

    state.resizeObserver.observe(mapElement);
  }

  function clearMarkers() {
    if (!state.map || !state.currentMarkers.length) return;
    state.currentMarkers.forEach((marker) => {
      if (marker?.__tooltipInstance && typeof marker.__tooltipInstance.dispose === "function") {
        marker.__tooltipInstance.dispose();
      }
      try {
        state.map.markers.remove(marker);
      } catch (_error) {
        // El mapa puede estar desmontandose mientras limpiamos marcadores.
      }
    });
    state.currentMarkers = [];
  }

  function clearHistoricalLayers() {
    if (state.traceSource) state.traceSource.clear();
    if (state.historySource) state.historySource.clear();
  }

  function clearComparisonRoute() {
    state.comparisonRoutePositions = [];
    if (state.comparisonRouteSource) state.comparisonRouteSource.clear();
  }

  function getMarkerLabel(vehicle) {
    const text = String(vehicle.abbreviation || vehicle.name || "V").trim();
    return text.slice(0, 3).toUpperCase();
  }

  function addCurrentMarker(vehicle, position) {
    const markerElement = document.createElement("button");
    markerElement.type = "button";
    markerElement.className = "live-marker-current";
    markerElement.style.setProperty("--live-marker-color", vehicle.color || "#0ea5e9");
    markerElement.setAttribute("aria-label", vehicle.name || "Vehiculo");
    markerElement.innerHTML = `
      <span class="live-marker-pulse"></span>
      <span class="live-marker-core">${escapeHtml(getMarkerLabel(vehicle))}</span>
    `;

    if (bootstrap?.Tooltip) {
      markerElement.__tooltipInstance = new bootstrap.Tooltip(markerElement, {
        container: "body",
        placement: "top",
        trigger: "hover focus",
        title: String(vehicle.name || "Vehiculo").trim(),
      });
    }

    const marker = new atlas.HtmlMarker({
      htmlContent: markerElement,
      position,
    });

    state.map.markers.add(marker);
    state.currentMarkers.push(marker);
  }

  function getCurrentPosition(vehicle) {
    const lat = Number.parseFloat(vehicle?.location?.lat);
    const lng = Number.parseFloat(vehicle?.location?.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    return [lng, lat];
  }

  function getHistoryPositions(vehicle) {
    if (!Array.isArray(vehicle?.history)) return [];

    return vehicle.history
      .map((point) => {
        const lat = Number.parseFloat(point?.lat);
        const lng = Number.parseFloat(point?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
        return [lng, lat];
      })
      .filter(Boolean);
  }

  function addHistoryToSources(vehicle, position) {
    const historyPositions = getHistoryPositions(vehicle);
    const trackPositions = historyPositions.length ? historyPositions : [position];
    const color = vehicle.color || "#0ea5e9";

    if (trackPositions.length > 1 && state.traceSource) {
      const colorHex = color.replace("#", "");
      if (!state.arrowIconColors.has(colorHex)) {
        state.arrowIconColors.add(colorHex);
        state.map.imageSprite.add("route-arrow-" + colorHex, buildArrowDataUri(color));
      }
      const line = new atlas.data.Feature(
        new atlas.data.LineString(trackPositions),
        {
          glowColor: color,
          routeColorHex: colorHex,
          strokeColor: color,
        }
      );
      state.traceSource.add(line);
    }

    if (state.historySource) {
      trackPositions.forEach((trackPosition) => {
        const point = new atlas.data.Feature(
          new atlas.data.Point(trackPosition),
          {
            pointColor: color,
          }
        );
        state.historySource.add(point);
      });
    }
  }

  function createBoundsFromPositions(positions) {
    if (!positions.length || !window.atlas?.data?.BoundingBox?.fromPositions) {
      return null;
    }

    return atlas.data.BoundingBox.fromPositions(positions);
  }

  function getVisibleBoundsPositions() {
    const positions = Array.from(state.currentPositions.values());
    if (Array.isArray(state.comparisonRoutePositions) && state.comparisonRoutePositions.length) {
      positions.push(...state.comparisonRoutePositions);
    }
    return positions;
  }

  function fitToVisible(bounds) {
    if (!state.map || !bounds) return;
    state.lastBounds = bounds;
    state.map.setCamera({
      bounds,
      maxZoom: 13.5,
      padding: DEFAULT_PADDING,
    });
  }

  async function setVehicles(vehicles, options = {}) {
    await ensureMap();
    if (!state.map || state.destroyed) return;

    const preserveCamera = options?.preserveCamera === true;
    clearMarkers();
    clearHistoricalLayers();
    state.currentPositions = new Map();

    const visibleVehicles = (Array.isArray(vehicles) ? vehicles : []).filter(
      (vehicle) => vehicle?.visible !== false
    );
    const boundsPositions = [];

    visibleVehicles.forEach((vehicle) => {
      const position = getCurrentPosition(vehicle);
      if (!position) return;

      boundsPositions.push(position);
      boundsPositions.push(...getHistoryPositions(vehicle));
      state.currentPositions.set(String(vehicle.id), position);
      addCurrentMarker(vehicle, position);
      addHistoryToSources(vehicle, position);
    });

    if (Array.isArray(state.comparisonRoutePositions) && state.comparisonRoutePositions.length) {
      boundsPositions.push(...state.comparisonRoutePositions);
    }

    const bounds = createBoundsFromPositions(boundsPositions);
    if (bounds) {
      state.lastBounds = bounds;
      if (!preserveCamera) fitToVisible(bounds);
    }
  }

  async function setComparisonRoutes(routes, options = {}) {
    await ensureMap();
    if (!state.map || state.destroyed) return;
    const preserveCamera = options?.preserveCamera === true;
    clearComparisonRoute();

    const routeArray = Array.isArray(routes) ? routes.filter(Boolean) : [];
    if (!routeArray.length) {
      const bounds = createBoundsFromPositions(getVisibleBoundsPositions());
      if (bounds) {
        state.lastBounds = bounds;
        if (!preserveCamera) fitToVisible(bounds);
      }
      return;
    }

    const allPositions = [];
    await Promise.all(
      routeArray.map(async (route) => {
        const positions = getStoredRouteGeometry(route) || [];
        const returnPositions = getStoredReturnGeometry(route) || [];
        if (positions.length < 2 || !state.comparisonRouteSource) return;
        const routeColor = sanitizeHexColor(route?.route_color_hex || route?.color);
        const strokeColor = `#${lightenHexColor(routeColor, 0.28)}`;
        const colorHex = strokeColor.replace("#", "");
        if (!state.arrowIconColors.has(colorHex)) {
          state.arrowIconColors.add(colorHex);
          state.map.imageSprite.add("route-arrow-" + colorHex, buildArrowDataUri(strokeColor));
        }
        allPositions.push(...positions, ...returnPositions);
        state.comparisonRouteSource.add(
          new atlas.data.Feature(new atlas.data.LineString(positions), {
            strokeColor,
            glowColor: `#${lightenHexColor(routeColor, 0.4)}`,
            routeColorHex: colorHex,
          })
        );
        if (returnPositions.length > 1) {
          state.comparisonRouteSource.add(
            new atlas.data.Feature(new atlas.data.LineString(returnPositions), {
              strokeColor,
              isReturn: true,
            })
          );
        }
      })
    );

    state.comparisonRoutePositions = allPositions;
    const bounds = createBoundsFromPositions(getVisibleBoundsPositions());
    if (bounds) {
      state.lastBounds = bounds;
      if (!preserveCamera) fitToVisible(bounds);
    }
  }

  function focusVehicle(vehicleId) {
    if (!state.map) return;
    const position = state.currentPositions.get(String(vehicleId));
    if (!position) return;

    state.map.setCamera({
      center: position,
      duration: 500,
      type: "ease",
      zoom: Math.max(Number(state.map.getCamera().zoom || DEFAULT_ZOOM), 13),
    });
  }

  function zoomIn() {
    if (!state.map) return;
    const nextZoom = clampZoom(Number(state.map.getCamera().zoom || DEFAULT_ZOOM) + 1, DEFAULT_ZOOM);
    state.map.setCamera({ zoom: nextZoom });
  }

  function zoomOut() {
    if (!state.map) return;
    const nextZoom = clampZoom(Number(state.map.getCamera().zoom || DEFAULT_ZOOM) - 1, DEFAULT_ZOOM);
    state.map.setCamera({ zoom: nextZoom });
  }

  function fitMap() {
    const currentBounds = createBoundsFromPositions(getVisibleBoundsPositions());
    if (currentBounds) {
      fitToVisible(currentBounds);
      return;
    }
    if (state.lastBounds) fitToVisible(state.lastBounds);
  }

  function destroy() {
    state.destroyed = true;
    clearTimeout(state.resizeTimer);
    if (state.resizeObserver) {
      state.resizeObserver.disconnect();
      state.resizeObserver = null;
    }
    clearMarkers();
    clearComparisonRoute();
    state.arrowIconColors = new Set();
    state.traceSource = null;
    state.historySource = null;
    state.comparisonRouteSource = null;
    state.currentPositions = new Map();
    state.lastBounds = null;
    if (state.map) {
      try {
        state.map.dispose();
      } catch (_error) {
        // Si el SDK ya esta liberando recursos, evitamos propagar errores de dispose.
      }
    }
    state.map = null;
    state.mapReady = null;
  }

  function setStyle(style) {
    if (!state.map || !style || state.destroyed) {
      return;
    }

    try {
      state.map.setStyle({ style });
    } catch (_error) {
      // Ignoramos cambios de estilo tardios cuando la vista esta saliendo.
    }
  }

  return {
    destroy,
    fitMap,
    focusVehicle,
    setComparisonRoutes,
    setVehicles,
    setStyle,
    zoomIn,
    zoomOut,
  };
}

